import { useEffect, useState } from "react";
import { X, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { extractApiError } from "@/components/PanelState";

// Shared UI atoms for Tech Profile CRUD. Used by both the platform-admin CRUD
// page and the tenant-scoped page (which gained CRUD rights when call_center
// was widened to full CRUD on 2026-07-31 per Brett). Keeping the form + delete
// confirm in one place means the two surfaces stay visually + behaviorally
// identical and the audit-log payload doesn't drift between callers.

export const TIERS = [
  { value: "top",         label: "Top" },
  { value: "mid",         label: "Mid" },
  { value: "developing",  label: "Developing" },
];

export const LICENSES = [
  { value: "master",         label: "Master" },
  { value: "journeyman",     label: "Journeyman" },
  { value: "non_journeyman", label: "Non-Journeyman" },
  { value: "apprentice",     label: "Apprentice" },
];

export const LICENSE_LABEL = {
  master: "Master",
  journeyman: "Journeyman",
  non_journeyman: "Non-Journeyman",
  apprentice: "Apprentice",
};

export const TIER_TONE = {
  top:         "bg-[#e6f9f2] text-[#0e8a68] border-[#c1eddc]",
  mid:         "bg-[#e7f4fb] text-[#1c6a99] border-[#c5e0ee]",
  developing:  "bg-[#fff4d6] text-[#8a5b00] border-[#f3d97a]",
};

const INPUT_CLS = "w-full border border-[#e7e9ee] rounded-lg px-3 py-2 text-[13px] outline-none focus:border-[#0072bc]";
const LABEL_CLS = "text-[11px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold";

// Comma-separated → array of trimmed non-empty strings.
export function csvToArr(s) {
  return String(s || "").split(",").map((x) => x.trim()).filter(Boolean);
}
export function arrToCsv(a) {
  return (Array.isArray(a) ? a : []).join(", ");
}

const EMPTY_FORM = {
  external_id: "",
  name: "",
  tier: "mid",
  license: "journeyman",
  home_territory: "",
  cross_territory: false,
  alt_availability: "",
  relationship_tags: "",
  avoid_tags: "",
  communication_style: "",
  notes: "",
  active: true,
  // pt.75 (2026-09-15): payroll-critical fields — Tech Profiles is now
  // the single source of truth for tech data (payroll_roster retired).
  pay_type: "commission",
  team_leader: false,
  hourly_rate: "",
  aliases: "",
  email: "",
  fixed_commission_pct: "",
};


// ============================================================================
// Create / Edit modal — shared between admin CRUD page and tenant CRUD page.
// avoid_tags writes are always audited server-side under the caller's real
// user (get_current_user), so no extra client-side wiring is needed to make
// call_center's edits attribute correctly in admin_audit_log.
// ============================================================================
export function TechProfileFormModal({ tenantSlug, initial, onClose, onSaved }) {
  const isEdit = Boolean(initial?.id);
  // 2026-08-10: fetch tenant-defined custom field defs so the modal renders
  // the correct input control per field_type. Values are saved separately
  // via PATCH /tech-profiles/{id}/custom-fields after the core save so a
  // schema-validation failure doesn't reject the main profile update.
  const [customDefs, setCustomDefs] = useState([]);
  const [customValues, setCustomValues] = useState(initial?.custom_fields || {});
  useEffect(() => {
    api.get("/tech-profile-fields", { params: { tenant_slug: tenantSlug } })
      .then(({ data }) => setCustomDefs(Array.isArray(data) ? data.filter((d) => !d.archived) : []))
      .catch(() => setCustomDefs([]));
  }, [tenantSlug]);
  const setCustomVal = (fid, v) => setCustomValues((prev) => ({ ...prev, [fid]: v }));
  const [form, setForm] = useState(() => {
    if (!isEdit) return EMPTY_FORM;
    return {
      external_id: initial.external_id || "",
      name: initial.name || "",
      tier: initial.tier || "mid",
      license: initial.license || "journeyman",
      home_territory: arrToCsv(initial.home_territory),
      cross_territory: !!initial.cross_territory,
      alt_availability: initial.alt_availability || "",
      relationship_tags: arrToCsv(initial.relationship_tags),
      avoid_tags: arrToCsv(initial.avoid_tags),
      communication_style: initial.communication_style || "",
      notes: initial.notes || "",
      active: initial.active !== false,
      pay_type: initial.pay_type || "commission",
      team_leader: !!initial.team_leader,
      hourly_rate: initial.hourly_rate != null ? String(initial.hourly_rate) : "",
      aliases: arrToCsv(initial.aliases),
      email: initial.email || "",
      fixed_commission_pct: initial.fixed_commission_pct != null ? String(initial.fixed_commission_pct) : "",
    };
  });
  // CC-171: only payroll editors / platform_admin may set the fixed % override.
  const [canSetFixedPct, setCanSetFixedPct] = useState(false);
  useEffect(() => {
    api.get("/payroll/viewer").then(({ data }) => setCanSetFixedPct(!!data?.is_payroll_editor)).catch(() => setCanSetFixedPct(false));
  }, []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    const payload = {
      external_id: form.external_id.trim() || null,
      name: form.name.trim(),
      tier: form.tier,
      license: form.license,
      home_territory: csvToArr(form.home_territory),
      cross_territory: !!form.cross_territory,
      alt_availability: form.alt_availability.trim() || null,
      relationship_tags: csvToArr(form.relationship_tags),
      avoid_tags: csvToArr(form.avoid_tags),
      communication_style: form.communication_style.trim() || null,
      notes: form.notes.trim() || null,
      active: !!form.active,
      pay_type: form.pay_type,
      team_leader: !!form.team_leader,
      hourly_rate: form.hourly_rate === "" ? null : parseFloat(form.hourly_rate),
      aliases: csvToArr(form.aliases),
      email: form.email.trim() || null,
    };
    const fixedPct = form.fixed_commission_pct === "" ? null : parseFloat(form.fixed_commission_pct);
    if (canSetFixedPct && fixedPct !== (initial?.fixed_commission_pct ?? null)) payload.fixed_commission_pct = fixedPct;
    try {
      let savedId = initial?.id;
      if (isEdit) {
        const delta = { ...payload };
        delete delta.external_id;  // external_id is the dedupe key; can't be re-slotted.
        await api.patch(`/tech-profiles/${initial.id}`, delta, { params: { tenant_slug: tenantSlug } });
      } else {
        const { data } = await api.post("/tech-profiles", payload, { params: { tenant_slug: tenantSlug } });
        savedId = data?.id;
      }
      // Persist custom-field values in a second call. Kept separate from the
      // main profile CRUD so a bad option value doesn't reject an otherwise
      // valid name/tier/license update.
      if (savedId && customDefs.length > 0) {
        const values = {};
        for (const d of customDefs) {
          const v = customValues[d.id];
          // Include nulls to explicitly clear a previously-set value.
          if (v === "" || v === undefined) {
            values[d.id] = null;
          } else {
            values[d.id] = v;
          }
        }
        await api.patch(`/tech-profiles/${savedId}/custom-fields`, { values }, { params: { tenant_slug: tenantSlug } });
      }
      await onSaved();
    } catch (err) {
      setError(extractApiError(err)?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center p-4 overflow-y-auto"
      data-testid="tech-profile-modal"
      onClick={onClose}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-xl w-full max-w-[720px] shadow-xl border border-[#e7e9ee] my-8"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#e7e9ee]">
          <div>
            <h2 className="font-display font-bold text-[18px] text-[#0e1420]">
              {isEdit ? "Edit tech profile" : "New tech profile"}
            </h2>
            <p className="text-[12px] text-[color:var(--cc-ink-muted)] mt-0.5">
              {isEdit ? initial.name : "Adds a technician to the Dispatch Assist roster."}
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 hover:bg-[#f5f6f8] rounded-md" data-testid="tech-profile-modal-close">
            <X size={18} strokeWidth={1.8} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Name" required>
              <input data-testid="tp-field-name" className={INPUT_CLS} value={form.name}
                onChange={(e) => setF("name", e.target.value)} required maxLength={200} />
            </Field>
            <Field label="External ID (optional)" hint={isEdit ? "read-only after create" : "VPS-side unique ID; leave blank if unknown"}>
              <input data-testid="tp-field-external-id" className={INPUT_CLS} value={form.external_id}
                onChange={(e) => setF("external_id", e.target.value)} disabled={isEdit} />
            </Field>
            <Field label="Tier" required>
              <select data-testid="tp-field-tier" className={INPUT_CLS} value={form.tier}
                onChange={(e) => setF("tier", e.target.value)}>
                {TIERS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </Field>
            <Field label="License" required>
              <select data-testid="tp-field-license" className={INPUT_CLS} value={form.license}
                onChange={(e) => setF("license", e.target.value)}>
                {LICENSES.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
              </select>
            </Field>
            <Field label="Pay type" required hint="Payroll tier logic; hourly bypasses commission math">
              <select data-testid="tp-field-pay-type" className={INPUT_CLS} value={form.pay_type}
                onChange={(e) => setF("pay_type", e.target.value)}>
                <option value="commission">Commission</option>
                <option value="hourly">Hourly</option>
              </select>
            </Field>
            <Field label="Hourly rate ($/hr)" hint="Required when pay type is hourly">
              <input data-testid="tp-field-hourly-rate" className={INPUT_CLS}
                type="number" step="0.01" min="0" value={form.hourly_rate}
                disabled={form.pay_type !== "hourly"}
                onChange={(e) => setF("hourly_rate", e.target.value)} />
            </Field>
            <Field label={form.active ? "Email (required)" : "Email"} hint="Used by magic-link tech-review to send this tech their per-period breakdown">
              <input data-testid="tp-field-email" className={INPUT_CLS}
                type="email" value={form.email}
                required={form.active}
                onChange={(e) => setF("email", e.target.value)} />
            </Field>
          </div>

          <Field label="Fixed commission % override (optional)"
                 hint={canSetFixedPct
                   ? "Leave blank for the license rate. e.g. 21.5 = 21.5% flat, replaces the license rate, no Team Leader point, exempt from the alt-key streak. Weeks from 10/12 on."
                   : "Set by payroll editors only."}>
            <input data-testid="tp-field-fixed-commission-pct" className={INPUT_CLS}
              type="number" step="0.1" min="0.1" max="99.9" placeholder="Off"
              value={form.fixed_commission_pct} disabled={!canSetFixedPct}
              onChange={(e) => setF("fixed_commission_pct", e.target.value)} />
          </Field>

          <Field label="Aliases (comma-separated)" hint="ST-name variants used to match Primary Technician on the Commission report">
            <input data-testid="tp-field-aliases" className={INPUT_CLS}
              value={form.aliases} onChange={(e) => setF("aliases", e.target.value)} />
          </Field>

          <div className="flex items-center gap-4 flex-wrap">
            <label className="flex items-center gap-2 text-[12.5px] cursor-pointer select-none">
              <input type="checkbox" data-testid="tp-field-team-leader"
                checked={form.team_leader}
                onChange={(e) => setF("team_leader", e.target.checked)}
                className="accent-[#0072bc]" />
              Team Leader (+1% payroll bonus)
            </label>
          </div>

          <Field label="Home territory (comma-separated)" hint="e.g. Springfield, Joplin">
            <input data-testid="tp-field-home-territory" className={INPUT_CLS}
              value={form.home_territory} onChange={(e) => setF("home_territory", e.target.value)} />
          </Field>

          <div className="flex items-center gap-4 flex-wrap">
            <label className="flex items-center gap-2 text-[12.5px] cursor-pointer select-none">
              <input type="checkbox" data-testid="tp-field-cross-territory"
                checked={form.cross_territory}
                onChange={(e) => setF("cross_territory", e.target.checked)}
                className="accent-[#0072bc]" />
              Cross-territory OK
            </label>
            <label className="flex items-center gap-2 text-[12.5px] cursor-pointer select-none">
              <input type="checkbox" data-testid="tp-field-active"
                checked={form.active}
                onChange={(e) => setF("active", e.target.checked)}
                className="accent-[#0072bc]" />
              Active (hireable now)
            </label>
          </div>

          <Field label="Alternate availability" hint="e.g. weekends, evenings only">
            <input data-testid="tp-field-alt-availability" className={INPUT_CLS}
              value={form.alt_availability} onChange={(e) => setF("alt_availability", e.target.value)} />
          </Field>

          <Field label="Relationship tags (comma-separated)" hint="e.g. Katt-preferred, VIP-customer-friendly">
            <input data-testid="tp-field-relationship-tags" className={INPUT_CLS}
              value={form.relationship_tags} onChange={(e) => setF("relationship_tags", e.target.value)} />
          </Field>

          <Field label="Avoid tags (audited, comma-separated)" hint="e.g. no-commercial, no-permit. Writes emit admin_audit_log rows.">
            <input data-testid="tp-field-avoid-tags" className={INPUT_CLS}
              value={form.avoid_tags} onChange={(e) => setF("avoid_tags", e.target.value)} />
          </Field>

          <Field label="Communication style">
            <input data-testid="tp-field-communication-style" className={INPUT_CLS}
              value={form.communication_style} onChange={(e) => setF("communication_style", e.target.value)} />
          </Field>

          <Field label="Notes">
            <textarea data-testid="tp-field-notes" className={INPUT_CLS}
              value={form.notes} onChange={(e) => setF("notes", e.target.value)} rows={3} />
          </Field>

          {customDefs.length > 0 && (
            <div className="pt-3 border-t border-[#eef1f6]">
              <div className="text-[11px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold mb-3">
                Custom fields
              </div>
              <div className="grid sm:grid-cols-2 gap-4" data-testid="tp-custom-fields-inputs">
                {[...customDefs].sort((a, b) => (a.sort_order - b.sort_order) || a.label.localeCompare(b.label)).map((d) => (
                  <CustomFieldInput
                    key={d.id}
                    def={d}
                    value={customValues[d.id]}
                    onChange={(v) => setCustomVal(d.id, v)}
                  />
                ))}
              </div>
            </div>
          )}

          {error && (
            <div className="bg-[#fff5f5] border border-[#f5c2c2] rounded-lg p-3 text-[12.5px] text-[#7a1c1c]" data-testid="tech-profile-modal-error">
              {error}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-[#e7e9ee] bg-[#f9fafb] rounded-b-xl">
          <button type="button" onClick={onClose}
            className="px-4 py-2 text-[12.5px] font-semibold rounded-lg border border-[#e7e9ee] hover:bg-white">
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving || !form.name.trim()}
            data-testid="tech-profile-modal-save"
            className="px-4 py-2 text-[12.5px] font-semibold rounded-lg bg-[#0072bc] text-white hover:bg-[#005a95] disabled:opacity-50"
          >
            {saving ? "Saving…" : isEdit ? "Save changes" : "Create profile"}
          </button>
        </div>
      </form>
    </div>
  );
}


// ============================================================================
// Delete-confirm modal — matters more now that call_center can delete profiles.
// Two-line copy ("Are you sure you want to remove [name] from the roster?" +
// "This action cannot be undone.") gives the CSR a pause + an explicit undo
// warning, which the previous window.confirm() shortcut didn't communicate as
// clearly. Handles its own busy state so the caller doesn't need to double-book.
// ============================================================================
export function TechProfileDeleteConfirm({ tenantSlug, profile, onClose, onDeleted }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const doDelete = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.delete(`/tech-profiles/${profile.id}`, { params: { tenant_slug: tenantSlug } });
      await onDeleted();
    } catch (err) {
      setError(extractApiError(err)?.message || "Delete failed");
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      data-testid="tech-profile-delete-modal"
      onClick={busy ? undefined : onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-xl w-full max-w-[440px] shadow-xl border border-[#e7e9ee]"
      >
        <div className="p-5 border-b border-[#e7e9ee] flex items-start gap-3">
          <div className="shrink-0 w-10 h-10 rounded-full bg-[#fff5f5] flex items-center justify-center">
            <AlertTriangle size={20} strokeWidth={1.8} className="text-[#7a1c1c]" />
          </div>
          <div className="flex-1">
            <h2 className="font-display font-bold text-[17px] text-[#0e1420]">Remove tech profile?</h2>
            <p className="text-[13px] text-[#0e1420] mt-1.5 leading-relaxed">
              Are you sure you want to remove <b data-testid="tech-profile-delete-name">{profile.name}</b> from the roster?
            </p>
            <p className="text-[12px] text-[color:var(--cc-ink-muted)] mt-1.5">
              This action cannot be undone. Prefer flipping to <b>inactive</b> via Edit if the tech may return — deletion loses tier, tags, notes, and audit-log linkage.
            </p>
            {error && (
              <div className="mt-3 bg-[#fff5f5] border border-[#f5c2c2] rounded-lg p-2.5 text-[12px] text-[#7a1c1c]"
                data-testid="tech-profile-delete-error">
                {error}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-3 bg-[#f9fafb] rounded-b-xl">
          <button
            type="button" onClick={onClose} disabled={busy}
            data-testid="tech-profile-delete-cancel"
            className="px-4 py-2 text-[12.5px] font-semibold rounded-lg border border-[#e7e9ee] hover:bg-white disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button" onClick={doDelete} disabled={busy}
            data-testid="tech-profile-delete-confirm"
            className="px-4 py-2 text-[12.5px] font-semibold rounded-lg bg-[#7a1c1c] text-white hover:bg-[#5c1414] disabled:opacity-50"
          >
            {busy ? "Removing…" : `Remove ${profile.name}`}
          </button>
        </div>
      </div>
    </div>
  );
}


function Field({ label, hint, required, children }) {
  return (
    <label className="block space-y-1">
      <span className={LABEL_CLS}>
        {label}{required && <span className="text-[#7a1c1c] ml-0.5">*</span>}
      </span>
      {children}
      {hint && <span className="block text-[11px] text-[color:var(--cc-ink-muted)]">{hint}</span>}
    </label>
  );
}


// Renders the right input control per field_type. `value` is whatever's
// stored on the profile (string/number/bool/list); we coerce back to that
// shape on onChange so /custom-fields validates cleanly.
function CustomFieldInput({ def, value, onChange }) {
  const testid = `tp-custom-${def.key}`;
  if (def.field_type === "boolean") {
    return (
      <Field label={def.label} hint={def.required ? "Required" : undefined}>
        <select
          data-testid={testid}
          className={INPUT_CLS}
          value={value === true ? "true" : value === false ? "false" : ""}
          onChange={(e) => {
            const v = e.target.value;
            onChange(v === "" ? "" : v === "true");
          }}
        >
          <option value="">—</option>
          <option value="true">Yes</option>
          <option value="false">No</option>
        </select>
      </Field>
    );
  }
  if (def.field_type === "select") {
    return (
      <Field label={def.label} hint={def.required ? "Required" : undefined}>
        <select
          data-testid={testid}
          className={INPUT_CLS}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">—</option>
          {(def.options || []).map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      </Field>
    );
  }
  if (def.field_type === "multiselect") {
    const current = Array.isArray(value) ? value : [];
    return (
      <Field label={def.label} hint={def.required ? "Required · multi-select" : "multi-select"}>
        <div data-testid={testid} className="flex flex-wrap gap-1.5">
          {(def.options || []).map((opt) => {
            const active = current.includes(opt);
            return (
              <button
                key={opt}
                type="button"
                onClick={() => {
                  const next = active ? current.filter((x) => x !== opt) : [...current, opt];
                  onChange(next);
                }}
                data-testid={`${testid}-opt-${opt}`}
                className={`text-[11.5px] px-2.5 py-1 rounded-full border ${
                  active
                    ? "bg-[#0072bc] text-white border-[#0072bc]"
                    : "bg-white text-[#0e1420] border-[#e7e9ee] hover:bg-[#f5f6f8]"
                }`}
              >
                {opt}
              </button>
            );
          })}
        </div>
      </Field>
    );
  }
  if (def.field_type === "number") {
    return (
      <Field label={def.label} hint={def.required ? "Required" : undefined}>
        <input
          data-testid={testid}
          type="number"
          className={INPUT_CLS}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
        />
      </Field>
    );
  }
  // text (default)
  return (
    <Field label={def.label} hint={def.required ? "Required" : undefined}>
      <input
        data-testid={testid}
        className={INPUT_CLS}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
      />
    </Field>
  );
}
